import React from 'react';
import type { OMSOrder } from '../types';
import { useOMS } from '../OMSProvider';
import { format } from 'date-fns';
import { useMarketStore } from '../../../services/store';

export const OrderHistoryTable: React.FC<{ selectedStrategyId?: string, providedOrders?: OMSOrder[] }> = ({ selectedStrategyId, providedOrders }) => {
    const { orders: contextOrders } = useOMS();
    const orders = providedOrders || contextOrders;
    // Subscribe to lastUpdated (primitive) so this table re-renders on new ticks,
    // but read the heavy `data` object imperatively via getState() to avoid
    // subscribing to the entire chain (which would force a re-render every tick).
    useMarketStore(state => state.lastUpdated);
    const marketData = useMarketStore.getState().data;

    const filteredOrders = selectedStrategyId
        ? orders.filter(o => o.strategy_id === selectedStrategyId)
        : orders;

    if (filteredOrders.length === 0) {
        return (
            <div className="p-8 text-center bg-white/50 dark:bg-slate-900/30 backdrop-blur-md rounded-2xl border border-slate-200/50 dark:border-slate-700/30">
                <div className="text-slate-400 dark:text-slate-500 mb-2 font-medium">No Orders Found</div>
                <p className="text-xs text-slate-400">Waiting for strategy signals to trigger orders.</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto bg-white/70 dark:bg-slate-900/50 backdrop-blur-2xl rounded-2xl border border-slate-200/50 dark:border-slate-700/30 shadow-sm">
            <table className="w-full text-left border-collapse">
                <thead>
                    <tr className="bg-slate-50/80 dark:bg-slate-800/80 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-700/50">
                        <th className="p-3 font-semibold rounded-tl-xl">SL No</th>
                        <th className="p-3 font-semibold">Date & Time</th>
                        <th className="p-3 font-semibold">Type</th>
                        <th className="p-3 font-semibold">Strike / Opt</th>
                        <th className="p-3 font-semibold text-right">Entry Price</th>
                        <th className="p-3 font-semibold text-right">Qty</th>
                        <th className="p-3 font-semibold text-right">Capital</th>
                        <th className="p-3 font-semibold">Entry Remarks</th>
                        <th className="p-3 font-semibold">Exit Time</th>
                        <th className="p-3 font-semibold text-right">Exit Price</th>
                        <th className="p-3 font-semibold text-right">Exit Capital</th>
                        <th className="p-3 font-semibold">Exit Remarks</th>
                        <th className="p-3 font-semibold text-right">Order PL</th>
                        <th className="p-3 font-semibold text-right">Charges</th>
                        <th className="p-3 font-semibold text-right rounded-tr-xl">Net PL</th>
                    </tr>
                </thead>
                <tbody className="text-xs divide-y divide-slate-100 dark:divide-slate-800/50">
                    {filteredOrders.map((order, idx) => {
                        const capital = order.entry_price * order.qty;
                        let exitPrice = order.exit_price;
                        let isLivePL = false;

                        if (order.status === 'OPEN' && marketData) {
                            const chain = marketData[order.symbol] || marketData['NSE_INDEX|' + order.symbol] || marketData['BSE_INDEX|' + order.symbol];
                            if (chain && Array.isArray(chain)) {
                                const row: any = chain.find((r: any) => r.strike === order.strike || r.strikePrice === order.strike);
                                if (row) {
                                    exitPrice = order.option_type === 'CE' ? (row.ce?.ltp || row.callLtp) : (row.pe?.ltp || row.putLtp);
                                    if (exitPrice) isLivePL = true;
                                }
                            }
                        }

                        const exitCapital = exitPrice ? exitPrice * order.qty : 0;
                        const computedOrderPl = exitCapital ? (exitCapital - capital) : 0;
                        const computedNetPl = exitCapital ? (computedOrderPl - (order.charges || 0)) : 0;

                        const isWin = computedNetPl > 0;
                        const isLoss = computedNetPl < 0;
                        const plColor = isWin ? 'text-emerald-600 dark:text-emerald-400' : isLoss ? 'text-rose-600 dark:text-rose-400' : 'text-slate-600 dark:text-slate-400';
                        
                        return (
                            <tr key={order.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${order.status === 'OPEN' ? 'bg-indigo-50/30 dark:bg-indigo-900/10' : ''}`}>
                                <td className="p-3 font-medium text-slate-500 dark:text-slate-400">
                                    {idx + 1}
                                </td>
                                <td className="p-3 font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                                    {format(new Date(order.entry_time), 'MMM dd, HH:mm:ss')}
                                </td>
                                <td className="p-3">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${order.trade_type === 'BULL' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'}`}>
                                        {order.trade_type}
                                    </span>
                                </td>
                                <td className="p-3 font-bold text-slate-800 dark:text-slate-200">
                                    {order.strike} <span className={order.option_type === 'CE' ? 'text-emerald-600' : 'text-rose-600'}>{order.option_type}</span>
                                </td>
                                <td className="p-3 text-right font-medium text-slate-700 dark:text-slate-300">₹{order.entry_price.toFixed(2)}</td>
                                <td className="p-3 text-right text-slate-600 dark:text-slate-400">{order.qty}</td>
                                <td className="p-3 text-right font-medium text-slate-700 dark:text-slate-300">₹{capital.toFixed(2)}</td>
                                <td className="p-3 text-[10px] text-slate-500 max-w-[200px] whitespace-normal break-words leading-tight" title={order.entry_remarks || ''}>{order.entry_remarks}</td>
                                
                                <td className="p-3 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                    {order.exit_time ? format(new Date(order.exit_time), 'HH:mm:ss') : <span className="text-indigo-500 font-semibold animate-pulse">OPEN</span>}
                                </td>
                                <td className="p-3 text-right font-medium text-slate-700 dark:text-slate-300">
                                    {exitPrice ? <span className={isLivePL ? "animate-pulse text-indigo-500" : ""}>₹{exitPrice.toFixed(2)}</span> : '-'}
                                </td>
                                <td className="p-3 text-right font-medium text-slate-700 dark:text-slate-300">
                                    {exitCapital ? <span className={isLivePL ? "animate-pulse text-indigo-500" : ""}>₹{exitCapital.toFixed(2)}</span> : '-'}
                                </td>
                                <td className="p-3 text-[10px] text-slate-500 max-w-[200px] whitespace-normal break-words leading-tight" title={order.exit_remarks || ''}>{order.exit_remarks || '-'}</td>
                                
                                <td className="p-3 text-right font-medium text-slate-600 dark:text-slate-400">
                                    {computedOrderPl ? <span className={isLivePL ? "animate-pulse text-indigo-500" : ""}>₹{computedOrderPl.toFixed(2)}</span> : '-'}
                                </td>
                                <td className="p-3 text-right text-slate-400 dark:text-slate-500">₹{Number(order.charges || 0).toFixed(2)}</td>
                                <td className={`p-3 text-right font-black ${plColor}`}>
                                    {computedNetPl ? <span className={isLivePL ? "animate-pulse text-indigo-500" : ""}>₹{computedNetPl.toFixed(2)}</span> : '-'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
